import { motion } from "framer-motion";
import {
  BadgeCheck, BellRing, Bot, Check, ChevronDown, Database, Globe, Home,
  Megaphone, MessageCircle, Plus, Target, Workflow,
} from "lucide-react";
import "@/App.css";
import { useLenis } from "@/hooks/useLenis";
import { Navbar } from "@/components/Navbar";
import { Marquee } from "@/components/Marquee";
import { Scene3D } from "@/components/Scene3D";
import { ToolsMarquee } from "@/components/ToolsMarquee";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import { Eyebrow, MaskedLine, Reveal } from "@/components/Reveal";
import { WhatsAppIcon } from "@/components/icons";
import { Economize } from "@/sections/Economize";
import { Metodo } from "@/sections/Metodo";
import { Portfolio } from "@/sections/Portfolio";
import { Contato } from "@/sections/Contato";
import { Footer } from "@/sections/Footer";
import { FLAGSHIP_FEATURES, SERVICES, WHATSAPP_URL, waLink } from "@/data/site";

const EASE = [0.22, 1, 0.36, 1];
const SERVICE_ICONS = { Globe, Bot, House: Home, Target, Megaphone, MessageCircle, Workflow };

const HeroCenter = () => (
  <section id="hero" className="relative flex min-h-screen items-end justify-center overflow-hidden">
    <div className="pointer-events-none absolute inset-0 z-[1] bg-[radial-gradient(70%_60%_at_50%_30%,rgba(0,90,220,0.22),transparent_70%)]" />
    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-56 bg-gradient-to-t from-[#05070D] to-transparent" />

    <div className="relative z-10 mx-auto w-full max-w-5xl px-6 pb-16 pt-[44vh] text-center lg:px-10">
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.9 }}
        className="mb-7 text-[11px] font-medium uppercase tracking-[0.4em] text-slate-400"
      >
        IA <span className="text-[#3a9bff]">•</span> Automações <span className="text-[#3a9bff]">•</span> Marketing
      </motion.p>
      <h1 className="text-4xl font-semibold leading-[1.06] tracking-tight text-slate-50 sm:text-5xl lg:text-7xl">
        <MaskedLine delay={0.15}>Seu negócio rodando</MaskedLine>
        <MaskedLine delay={0.32}>no <span className="text-[#3a9bff]">automático.</span></MaskedLine>
      </h1>
      <motion.p
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
        className="mx-auto mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
      >
        Sites, automações e inteligência artificial sob medida para pequenos negócios
        que querem crescer com estrutura, e não no improviso.
      </motion.p>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
        className="mt-10 flex flex-wrap items-center justify-center gap-4"
      >
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center gap-3 rounded-full bg-[#007BFF] px-8 py-4 text-sm font-semibold text-white transition-shadow duration-300 hover:shadow-[0_0_42px_rgba(0,123,255,0.6)]"
        >
          <WhatsAppIcon className="h-5 w-5 transition-transform duration-300 group-hover:scale-110" />
          Falar no WhatsApp
        </a>
        <a
          href="#fluxo"
          className="inline-flex items-center gap-2 rounded-full border border-white/15 px-8 py-4 text-sm font-medium text-slate-200 transition-colors duration-300 hover:border-[#007BFF]/60 hover:text-white"
        >
          Ver como funciona
          <ChevronDown className="h-4 w-4" />
        </a>
      </motion.div>
    </div>
  </section>
);

// Bento: carro-chefe ocupa 2x2, os 5 serviços preenchem o resto.
const SolucoesBento = () => (
  <section id="solucoes" className="relative scroll-mt-24 bg-[#05070D]/70 py-28 lg:py-36">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal className="text-center">
        <Eyebrow>Soluções</Eyebrow>
        <h2 className="mx-auto mt-5 max-w-3xl text-3xl font-semibold tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
          O que a AMB coloca de pé <span className="text-[#3a9bff]">para o seu negócio</span>
        </h2>
      </Reveal>

      <div className="mt-16 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Reveal className="md:col-span-2 lg:row-span-3">
          <div className="relative flex h-full flex-col overflow-hidden rounded-2xl border border-[#007BFF]/30 bg-gradient-to-br from-[#0a1830] via-[#0B0F14] to-[#05070D] p-8 sm:p-10">
            <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#007BFF]/20 blur-[90px]" />
            <span className="relative inline-flex w-fit items-center gap-2 rounded-full border border-[#007BFF]/50 bg-[#007BFF]/10 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#6db4ff]">
              <Workflow className="h-3.5 w-3.5" />
              Carro-chefe AMB
            </span>
            <h3 className="relative mt-6 text-2xl font-semibold leading-snug text-slate-50 sm:text-3xl">
              Automação de ponta a ponta
            </h3>
            <p className="relative mt-4 max-w-md text-base leading-relaxed text-slate-400">
              O cliente chama, a automação responde, o site converte e os sistemas conversam
              entre si, 24 horas por dia.
            </p>
            <ul className="relative mt-8 space-y-3">
              {FLAGSHIP_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-slate-300">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#3a9bff]" />
                  {f}
                </li>
              ))}
            </ul>
            <div className="relative mt-auto pt-10">
              <a
                href={waLink("Olá! Quero saber mais sobre a automação completa da AMB (WhatsApp, e-mail, site e integrações).")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 rounded-full bg-[#007BFF] px-7 py-3.5 text-sm font-semibold text-white transition-shadow duration-300 hover:shadow-[0_0_36px_rgba(0,123,255,0.55)]"
              >
                <WhatsAppIcon className="h-4 w-4" />
                Quero essa automação
              </a>
            </div>
          </div>
        </Reveal>

        {SERVICES.map((s, i) => {
          const Icon = SERVICE_ICONS[s.icon] ?? Globe;
          return (
            <Reveal key={s.name} delay={0.06 * i} className={i === SERVICES.length - 1 ? "md:col-span-2" : ""}>
              <div className="group h-full rounded-2xl border border-white/[0.07] bg-[#0d1219]/90 p-7 transition-[border-color,transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:border-[#007BFF]/40 hover:shadow-[0_18px_50px_rgba(0,60,160,0.18)]">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#007BFF]/25 bg-[#007BFF]/10 text-[#3a9bff] transition-transform duration-500 group-hover:scale-110">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 text-base font-semibold text-slate-100">{s.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const FLUXO = [
  { icon: MessageCircle, title: "Cliente chama", desc: "Mensagem chega no WhatsApp, Instagram ou site, a qualquer hora." },
  { icon: Bot, title: "IA responde", desc: "Atendimento imediato, no tom da sua marca, tirando as dúvidas iniciais." },
  { icon: Database, title: "CRM registra", desc: "Contato, interesse e histórico salvos sem ninguém digitar nada." },
  { icon: BellRing, title: "Follow-up", desc: "Lembretes e retornos automáticos para nenhum orçamento esfriar." },
  { icon: BadgeCheck, title: "Venda fechada", desc: "Seu time entra só na hora certa, com o cliente pronto para decidir." },
];

const Fluxo = () => (
  <section id="fluxo" className="relative scroll-mt-24 overflow-hidden border-y border-white/5 bg-[#080b11]/80 py-28 lg:py-36">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <Eyebrow>Na prática</Eyebrow>
        <h2 className="mt-5 max-w-2xl text-3xl font-semibold tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
          Como a automação <span className="text-[#3a9bff]">trabalha por você</span>
        </h2>
      </Reveal>

      <div className="relative mt-20">
        {/* linha que "corre" pelo fluxo ao entrar na tela */}
        <div className="absolute left-0 right-0 top-7 hidden h-px bg-white/[0.08] lg:block" />
        <motion.div
          className="absolute left-0 right-0 top-7 hidden h-px origin-left bg-gradient-to-r from-[#007BFF] to-[#3a9bff] shadow-[0_0_12px_rgba(0,123,255,0.8)] lg:block"
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 1.8, ease: EASE }}
        />
        <ol className="grid gap-10 lg:grid-cols-5 lg:gap-6">
          {FLUXO.map((step, i) => (
            <Reveal key={step.title} delay={0.25 * i} y={20}>
              <li className="relative flex gap-5 lg:block">
                <span className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-[#1e3a63] bg-[#05070D] text-[#3a9bff] transition-colors duration-500 hover:border-[#007BFF]">
                  <step.icon className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 lg:mt-6">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{step.desc}</p>
                </div>
              </li>
            </Reveal>
          ))}
        </ol>
      </div>
    </div>
  </section>
);

const FAQ = [
  {
    q: "Preciso trocar os sistemas que já uso?",
    a: "Não. A AMB integra o que você já tem (WhatsApp, planilhas, CRM, meios de pagamento) e só sugere trocar algo quando faz diferença real no resultado.",
  },
  {
    q: "A IA vai substituir o meu atendimento?",
    a: "Ela assume o repetitivo: dúvidas iniciais, agendamentos, lembretes. Seu time continua no controle e entra nas conversas que realmente pedem uma pessoa.",
  },
  {
    q: "Funciona para o meu tipo de negócio?",
    a: "Se o seu negócio atende clientes por mensagem, vende online ou repete tarefas toda semana, existe automação que se paga. O diagnóstico mostra onde.",
  },
  {
    q: "Quanto tempo leva para ficar pronto?",
    a: "Depende do escopo. No diagnóstico inicial definimos as prioridades, o prazo e o que entra primeiro, para você ver resultado cedo.",
  },
  {
    q: "Como começo?",
    a: "Chame no WhatsApp e peça o diagnóstico inicial. É uma conversa direta, sem formulário.",
  },
];

const Faq = () => (
  <section id="faq" className="relative scroll-mt-24 bg-[#05070D]/70 py-28 lg:py-36">
    <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-[0.8fr_1.2fr] lg:px-10">
      <Reveal>
        <Eyebrow>Dúvidas</Eyebrow>
        <h2 className="mt-5 text-3xl font-semibold tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
          Perguntas <span className="text-[#3a9bff]">frequentes</span>
        </h2>
        <p className="mt-6 max-w-sm text-base leading-relaxed text-slate-400">
          Não achou o que procurava? Fale com a gente no WhatsApp.
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="divide-y divide-white/[0.07] border-y border-white/[0.07]">
          {FAQ.map((item) => (
            <details key={item.q} className="group py-6">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-6 text-left text-base font-medium text-slate-100 transition-colors duration-300 hover:text-white [&::-webkit-details-marker]:hidden">
                {item.q}
                <Plus className="h-5 w-5 shrink-0 text-[#3a9bff] transition-transform duration-300 group-open:rotate-45" />
              </summary>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-400">{item.a}</p>
            </details>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

export function Page3() {
  useLenis();
  return (
    <div className="App min-h-screen bg-[#05070D] font-sora text-slate-200 antialiased">
      <Scene3D layout="center" />
      <div className="relative z-10">
        <Navbar />
        <main>
          <HeroCenter />
          <Marquee />
          <SolucoesBento />
          <Fluxo />
          <ToolsMarquee />
          <Economize />
          <Metodo />
          <Portfolio />
          <Faq />
          <Contato />
        </main>
        <Footer />
        <FloatingWhatsApp />
      </div>
    </div>
  );
}
